import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { IAbstractPresenter } from '@core/interfaces';
import { IModifiersComponent, IModifiersPresenter } from './interfaces';
import { ModifierDetail, ModifierGroupWrapper } from '@core/models';
import {
  calculateModifierQuantityPrice,
  createAddExtraModifierPatch,
  createNoAddModifierPatch,
  getFreeModifierCount,
  isModifierMaximumReached,
  isModifierMinimumUnmet,
} from '@core/domain/menu/rules';
import {
  canDecreaseModifierQuantity,
  canIncreaseModifierQuantity,
  hasSingleProductModifierGroups,
  isModifierFree as isModifierFreeRule,
  isModifierSelectionDisabled as isModifierSelectionDisabledRule,
  shouldShowModifierBasePrice as shouldShowModifierBasePriceRule,
  shouldShowModifierExtraPrice as shouldShowModifierExtraPriceRule,
} from '@features/home/domain';

@Injectable()
export class ModifiersPresenter 
  extends IAbstractPresenter<IModifiersComponent>
  implements IModifiersPresenter 
{
  constructor() {
    super();
  }

  dispatchActions(): void {}
  suscribeObservers(): void {}

  hasModifierGroups(): boolean {
    return hasSingleProductModifierGroups(this.view.modifierGroupsFormGroups.length);
  }

  getModifierGroupsValueByFormGroup(modifierGroup: FormGroup): ModifierGroupWrapper | null {
    return modifierGroup?.value ?? null;
  }

  minModifierSelected(modifierGroup: FormGroup): boolean {
    const group = this.getModifierGroupsValueByFormGroup(modifierGroup)?.grupoModificador ?? null;
    return isModifierMinimumUnmet(group);
  }

  maxModifierSelected(modifierGroup: FormGroup): boolean {
    const group = this.getModifierGroupsValueByFormGroup(modifierGroup)?.grupoModificador ?? null;
    return isModifierMaximumReached(group);
  }

  getMinimumBadgeClass(modifierGroup: FormGroup): string {
    return this.minModifierSelected(modifierGroup) ? 'badge-danger' : 'badge-success';
  }

  getMaximumBadgeClass(modifierGroup: FormGroup): string {
    return this.maxModifierSelected(modifierGroup) ? 'badge-danger' : 'badge-success';
  }

  getModifierGroupDetailsAsFormGroup(modifierGroup: FormGroup): FormGroup[] {
    return ((modifierGroup.get('grupoModificador.detallesmodificadores') as FormArray)?.controls as FormGroup[]) ?? [];
  }

  getModifierGroupDetailByFormGroup(modifierDetail: FormGroup): ModifierDetail | null {
    return modifierDetail?.value ?? null;
  }

  isModifierSelectionDisabled(modifierDetail: FormGroup, modifierGroup: FormGroup): boolean {
    const detail = this.getModifierGroupDetailByFormGroup(modifierDetail);
    return isModifierSelectionDisabledRule(detail?.addExtra, this.maxModifierSelected(modifierGroup));
  }

  shouldShowModifierBasePrice(modifierDetail: FormGroup): boolean {
    return shouldShowModifierBasePriceRule(this.getModifierGroupDetailByFormGroup(modifierDetail)?.isFree);
  }

  shouldShowFreeModifierLabel(modifierDetail: FormGroup): boolean {
    return isModifierFreeRule(this.getModifierGroupDetailByFormGroup(modifierDetail)?.isFree);
  }

  canDecreaseModifier(modifierDetail: FormGroup): boolean {
    const detail = this.getModifierGroupDetailByFormGroup(modifierDetail);
    return canDecreaseModifierQuantity(detail?.addExtra, detail?.cantidad);
  }

  canIncreaseModifier(modifierDetail: FormGroup): boolean {
    return canIncreaseModifierQuantity(this.getModifierGroupDetailByFormGroup(modifierDetail)?.addExtra);
  }

  isModifierDecreaseDisabled(modifierDetail: FormGroup): boolean {
    return !this.canDecreaseModifier(modifierDetail);
  }

  isModifierIncreaseDisabled(modifierDetail: FormGroup): boolean {
    return !this.canIncreaseModifier(modifierDetail);
  }

  shouldShowExtraPrice(modifierDetail: FormGroup): boolean {
    return shouldShowModifierExtraPriceRule(this.getModifierGroupDetailByFormGroup(modifierDetail)?.cantidad);
  }

  shouldHideExtraPrice(modifierDetail: FormGroup): boolean {
    return !this.shouldShowExtraPrice(modifierDetail);
  }

  onNoAddChange(event: Event, control: FormGroup): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    control.patchValue(createNoAddModifierPatch(control.value, target.checked));
  }

  onAddExtraChange(event: Event, control: FormGroup, modifierGroup: FormGroup): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const group = this.getModifierGroupsValueByFormGroup(modifierGroup)?.grupoModificador ?? null;
    control.patchValue(
      createAddExtraModifierPatch(
        control.value,
        target.checked,
        group?.gratis ?? 0,
        getFreeModifierCount(group)
      )
    );
  }

  decreaseDetail(modifierDetail: FormGroup): void {
    if (!this.canDecreaseModifier(modifierDetail)) return;
    this.changeDetailQuantity(modifierDetail, -1);
  }

  increaseDetail(modifierDetail: FormGroup): void {
    if (!this.canIncreaseModifier(modifierDetail)) return;
    this.changeDetailQuantity(modifierDetail, 1);
  }

  private changeDetailQuantity(modifierDetail: FormGroup, change: -1 | 1): void {
    const currentQuantity = Number.parseInt(String(modifierDetail.get('cantidad')?.value ?? 0), 10);
    const { quantity, price } = calculateModifierQuantityPrice(
      currentQuantity,
      change,
      modifierDetail.get('precioInicial')?.value
    );

    modifierDetail.patchValue({
      precio: price.toString(),
      cantidad: quantity.toString(),
    });
  }
}
